import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Formik, Form, Field, FieldArray, ErrorMessage } from "formik";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import { Button } from "react-bootstrap";
import { getQuote, getTakafulMasterData } from "../../features/product";
import { Loader } from "../common/loader";
import { getExcelDate } from "../../utils/commonHelper";
import { transformMembers } from "../../utils/takafulHelpers";
import Stepper from "../Stepper";

// ✅ Age categories config - NOW WITH i18n KEYS
const ageCategories = [
  { key: "no_of_adults_-_(age_18_or_above)", labelKey: "travel.page3.ageGroups.adults" },
  { key: "no_of_child_-_(age_16_or_below)", labelKey: "travel.page3.ageGroups.children" },
  { key: "no_of_seniors_-_(age_71_to_79)", labelKey: "travel.page3.ageGroups.seniors" },
];

// ✅ Calculate min/max DOB ranges
const getAgeGroupRanges = () => {
  const today = new Date();
  const getDateYearsAgo = (years) => {
    const date = new Date(today);
    date.setFullYear(date.getFullYear() - years);
    return date.toISOString().split("T")[0];
  };

  return {
    "no_of_adults_-_(age_18_or_above)": {
      min: getDateYearsAgo(70),
      max: getDateYearsAgo(18),
    },
    "no_of_child_-_(age_16_or_below)": {
      min: getDateYearsAgo(17),
      max: getDateYearsAgo(1),
    },
    "no_of_seniors_-_(age_71_to_79)": {
      min: getDateYearsAgo(79),
      max: getDateYearsAgo(71),
    },
  };
};

// ✅ Custom validation to check date is within allowed range
const validateAgeRange = (value, context) => {
  if (!value) {
    return true;
  }

  const selectedDate = new Date(value);
  const dateLimits = context.parent.dateLimits;

  if (!dateLimits) {
    return true;
  }

  const minDate = new Date(dateLimits.min);
  const maxDate = new Date(dateLimits.max);

  if (selectedDate < minDate || selectedDate > maxDate) {
    return context.createError({
      message: `Birth date must be between ${dateLimits.min} and ${dateLimits.max}`,
    });
  }

  return true;
};

// ✅ Yup validation schemas - WITH i18n MESSAGE KEYS
const createPersonSchema = (isFirstPerson, t) => {
  return Yup.object().shape({
    title: Yup.string()
      .required(t("travel.page3.validationMessages.titleRequired")),
    first_name: Yup.string()
      .required(t("travel.page3.validationMessages.firstNameRequired"))
      .min(2, t("travel.page3.validationMessages.firstNameMin")),
    last_name: Yup.string()
      .required(t("travel.page3.validationMessages.lastNameRequired"))
      .min(2, t("travel.page3.validationMessages.lastNameMin")),
    passport_number: Yup.string()
      .required(t("travel.page3.validationMessages.passportRequired")),
      // .min(5, t("travel.page3.validationMessages.passportMin")),
    insured_dob: Yup.date()
      .required(t("travel.page3.validationMessages.birthDateRequired"))
      .test("age-range", t("travel.page3.validationMessages.birthDateInvalid"), validateAgeRange),
    gender: Yup.string()
      .required(t("travel.page3.validationMessages.genderRequired")),
    civil_resident_id: Yup.string()
      .required(t("travel.page3.validationMessages.civilIdRequired")),
      // .min(5, t("travel.page3.validationMessages.civilIdMin")),
    nationality: isFirstPerson
      ? Yup.string().required(t("travel.page3.validationMessages.nationalityRequired"))
      : Yup.string(),
    governate: isFirstPerson
      ? Yup.string().required(t("travel.page3.validationMessages.governateRequired"))
      : Yup.string(),
    wilayat: isFirstPerson
      ? Yup.string().required(t("travel.page3.validationMessages.wilayatRequired"))
      : Yup.string(),
    email: isFirstPerson
      ? Yup.string()
          .email(t("travel.page3.validationMessages.emailInvalid"))
          .required(t("travel.page3.validationMessages.emailRequired"))
      : Yup.string(),
    mobile_no: isFirstPerson
      ? Yup.string().required(t("travel.page3.validationMessages.mobileRequired"))
      : Yup.string(),
  });
};

const syncMemDetailsWithCounts = (counts, setFieldValue) => {
  const productDataStr = localStorage.getItem("product_inquiry");
  const productData = JSON.parse(productDataStr);
  const formData = productData != null ? JSON.parse(productData.form_data) : null;
  const mobileNumber = formData?.mobile_no;
  const emailId = formData?.email;

  const ranges = getAgeGroupRanges();
  const newPeople = [];

  counts.forEach((c) => {
    for (let i = 0; i < c.value; i++) {
      newPeople.push({
        title: "",
        first_name: "",
        last_name: "",
        insured_dob: "",
        gender: "",
        civil_resident_id: "",
        nationality: "",
        governate: "",
        wilayat: "",
        passport_number: "",
        email: newPeople.length === 0 ? (i == 0 ? emailId : undefined) : undefined,
        mobile_no: newPeople.length === 0 ? (i == 0 ? mobileNumber : undefined) : undefined,
        groupKey: c.key,
        dateLimits: ranges[c.key],
      });
    }
  });

  setFieldValue("mem_details", newPeople);
};

const TakafulPersonDetailsFormFormik = () => {
  const { t } = useTranslation(); // ✅ Add translation hook
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    quoteLoader,
    takafulMasterDataLoader,
    takafulTitleMasterData,
    takafulNationalityCompanyMasterData,
    takafulGovernateCompanyMasterData,
    takafulWilayatCompanyMasterData,
  } = useSelector((state) => state.product);

  const prefillCounts = useMemo(() => {
    return location?.state?.prefillCounts || {};
  }, [location?.state]);

  const planData = useMemo(
    () => location?.state?.data || {},
    [location?.state]
  );

  useEffect(() => {
    dispatch(getTakafulMasterData("title"));
    dispatch(getTakafulMasterData("nationality"));
    dispatch(getTakafulMasterData("governate"));
  }, [dispatch]);

  const initialCounts = useMemo(() => {
    return ageCategories.map((c) => ({
      ...c,
      label: t(c.labelKey), // ✅ Get translated label
      value: prefillCounts[c.key] || 0,
    }));
  }, [prefillCounts, t]);

  const productDataStr = localStorage.getItem("product_inquiry");
  const productData = JSON.parse(productDataStr);
  const formData = JSON.parse(productData.form_data);
  const mobileNumber = formData.mobile_no;
  const emailId = formData.email;

  const initialMemDetails = useMemo(() => {
    const ranges = getAgeGroupRanges();
    const newPeople = [];

    initialCounts.forEach((c) => {
      for (let i = 0; i < c.value; i++) {
        newPeople.push({
          title: "",
          first_name: "",
          last_name: "",
          insured_dob: "",
          gender: "",
          civil_resident_id: "",
          nationality: "",
          governate: "",
          wilayat: "",
          passport_number: "",
          email: newPeople.length === 0 ? (i == 0 ? emailId : undefined) : undefined,
          mobile_no: newPeople.length === 0 ? (i == 0 ? mobileNumber : undefined) : undefined,
          groupKey: c.key,
          dateLimits: ranges[c.key],
        });
      }
    });

    return newPeople;
  }, [initialCounts]);

  const initialValues = useMemo(
    () => ({
      counts: initialCounts,
      mem_details: initialMemDetails,
    }),
    [initialCounts, initialMemDetails]
  );

  return (
    <div className="container py-4">
      {(quoteLoader || takafulMasterDataLoader) && (
        <Loader type="logo-gif" classList="overlay-loader" />
      )}

      <Formik
        initialValues={initialValues}
        validationSchema={Yup.lazy((values) => {
          return Yup.object().shape({
            counts: Yup.array().of(
              Yup.object().shape({
                key: Yup.string(),
                labelKey: Yup.string(),
                label: Yup.string(),
                value: Yup.number().min(0, "Must be 0 or greater"),
              })
            ),
            mem_details: Yup.array().of(
              Yup.lazy((mem, context) => {
                const fieldPath = context.path || "";
                const indexMatch = fieldPath.match(/\[(\d+)\]/);
                const index = indexMatch ? parseInt(indexMatch[1]) : -1;

                return createPersonSchema(index === 0, t); // ✅ Pass t to schema
              })
            ),
          });
        })}
        enableReinitialize={false}
        onSubmit={(values) => {
          let customerData = {
            insured_dob: getExcelDate(values.mem_details[0].insured_dob),
            civil_resident_id: values.mem_details[0].civil_resident_id,
            title: values.mem_details[0].title,
            first_name: values.mem_details[0].first_name,
            last_name: values.mem_details[0].last_name,
            email: values.mem_details[0].email,
            mobile_no: values.mem_details[0].mobile_no,
            governate: values.mem_details[0].governate,
            wilayat: values.mem_details[0].wilayat,
            gender: values.mem_details[0].gender,
            nationality: values.mem_details[0].nationality,
          };

          const mem_details = transformMembers(values?.mem_details);

          dispatch(
            getQuote({
              ...planData?.extracted_data,
              planData,
              insurance_company_product_mapping_id:
                planData?.insurance_company_product_mapping_id,
              company_id: planData?.company_id,
              customerData,
              passport_number: mem_details[0].member_pass_num,
              mem_details,
            })
          ).then((res) => {
            navigate("/checkout", { state: { existingData: res.payload } });
          });
        }}
      >
        {({ values, setFieldValue, errors, touched }) => (
          <Form>
            {/* Age group counts */}
            <h3 className="pb-3 fw-bold">
              {t("travel.page3.title")} {/* ✅ Translated heading */}
            </h3>
            <FieldArray name="counts">
              {() => (
                <div className="row">
                  {values.counts.map((c, index) => {
                    var adults = values.counts[0].value;
                    var children = values.counts[1].value;
                    var seniors = values.counts[2].value;

                    const isSeniorField =
                      c.key === "no_of_seniors_-_(age_71_to_79)";
                    const isAdultOrChildField =
                      c.key === "no_of_adults_-_(age_18_or_above)" ||
                      c.key === "no_of_child_-_(age_16_or_below)";

                    let isDisabled = false;

                    if (isAdultOrChildField && seniors > 0) {
                      isDisabled = true;
                    }

                    if (isSeniorField && (adults > 0 || children > 0)) {
                      isDisabled = true;
                    }

                    return (
                      <div className="col-md-3 col-sm-12" key={c.key}>
                        <label className="form-label">{c.label}</label>
                        <Field
                          type="number"
                          name={`counts.${index}.value`}
                          className="form-control element-style"
                          min="0"
                          disabled={isDisabled}
                          value={c.value}
                          onChange={(e) => {
                            const val = parseInt(e.target.value) || "";
                            setFieldValue(`counts.${index}.value`, val);

                            const updatedCounts = [...values.counts];
                            updatedCounts[index].value = val;
                            syncMemDetailsWithCounts(
                              updatedCounts,
                              setFieldValue
                            );
                          }}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </FieldArray>

            {/* Person details */}
            {values.mem_details.length > 0 && <hr className="my-4" />}
            <FieldArray name="mem_details">
              {() =>
                values.mem_details.map((person, index) => {
                  const personErrors = errors.mem_details?.[index] || {};
                  const personTouched = touched.mem_details?.[index] || {};
                  const hasErrors = Object.keys(personErrors).length > 0;

                  return (
                    <div className="card mb-4" key={index}>
                      <div className="card-header">
                        <strong>
                          {t("travel.page3.personCard.title")} {index + 1} – {person.label || values.counts.find((c) => c.key === person.groupKey)?.label}
                        </strong>
                        {hasErrors && (
                          <span className="text-danger ms-2">
                            {t("travel.page3.personCard.warning")}
                          </span>
                        )}
                      </div>
                      <div className="card-body row gx-3 gy-1">
                        {/* Title */}
                        <div className="col-md-4 col-sm-12">
                          <label className="form-label">
                            {t("travel.page3.formLabels.title")}
                            <span className="text-danger">
                              {t("travel.page3.formLabels.required")}
                            </span>
                          </label>
                          <Field
                            as="select"
                            name={`mem_details.${index}.title`}
                            className={`form-control element-style ${
                              personErrors.title && personTouched.title
                                ? "is-invalid"
                                : ""
                            }`}
                          >
                            <option value="">{t("travel.page3.formLabels.select")}</option>
                            {takafulTitleMasterData?.map((title, i) => (
                              <option key={i} value={title.name}>
                                {title.name}
                              </option>
                            ))}
                          </Field>
                          <ErrorMessage
                            name={`mem_details.${index}.title`}
                            component="div"
                            className="invalid-feedback d-block"
                          />
                        </div>

                        {/* First Name */}
                        <div className="col-md-4 col-sm-12">
                          <label className="form-label">
                            {t("travel.page3.formLabels.firstName")}
                            <span className="text-danger">
                              {t("travel.page3.formLabels.required")}
                            </span>
                          </label>
                          <Field
                            name={`mem_details.${index}.first_name`}
                            className={`form-control element-style ${
                              personErrors.first_name && personTouched.first_name
                                ? "is-invalid"
                                : ""
                            }`}
                          />
                          <ErrorMessage
                            name={`mem_details.${index}.first_name`}
                            component="div"
                            className="invalid-feedback d-block"
                          />
                        </div>

                        {/* Last Name */}
                        <div className="col-md-4 col-sm-12">
                          <label className="form-label">
                            {t("travel.page3.formLabels.lastName")}
                            <span className="text-danger">
                              {t("travel.page3.formLabels.required")}
                            </span>
                          </label>
                          <Field
                            name={`mem_details.${index}.last_name`}
                            className={`form-control element-style ${
                              personErrors.last_name && personTouched.last_name
                                ? "is-invalid"
                                : ""
                            }`}
                          />
                          <ErrorMessage
                            name={`mem_details.${index}.last_name`}
                            component="div"
                            className="invalid-feedback d-block"
                          />
                        </div>

                        {/* DOB */}
                        <div className="col-md-4 col-sm-12">
                          <label className="form-label">
                            {t("travel.page3.formLabels.birthDate")}
                            <span className="text-danger">
                              {t("travel.page3.formLabels.required")}
                            </span>
                          </label>
                          <Field
                            type="date"
                            name={`mem_details.${index}.insured_dob`}
                            className={`form-control element-style ${
                              personErrors.insured_dob && personTouched.insured_dob
                                ? "is-invalid"
                                : ""
                            }`}
                            min={person.dateLimits?.min}
                            max={person.dateLimits?.max}
                          />
                          <ErrorMessage
                            name={`mem_details.${index}.insured_dob`}
                            component="div"
                            className="invalid-feedback d-block"
                          />
                          <small className="text-muted">
                            {t("travel.page3.dateRange.allowed")} {person.dateLimits?.min} {t("travel.page3.dateRange.to")} {person.dateLimits?.max}
                          </small>
                        </div>

                        {/* Gender */}
                        <div className="col-md-4 col-sm-12">
                          <label className="form-label">
                            {t("travel.page3.formLabels.gender")}
                            <span className="text-danger">
                              {t("travel.page3.formLabels.required")}
                            </span>
                          </label>
                          <Field
                            as="select"
                            name={`mem_details.${index}.gender`}
                            className={`form-control element-style ${
                              personErrors.gender && personTouched.gender
                                ? "is-invalid"
                                : ""
                            }`}
                          >
                            <option value="">{t("travel.page3.formLabels.selectGender")}</option>
                            <option value="Male">{t("travel.page3.formLabels.male")}</option>
                            <option value="Female">{t("travel.page3.formLabels.female")}</option>
                            <option value="Others">{t("travel.page3.formLabels.others")}</option>
                          </Field>
                          <ErrorMessage
                            name={`mem_details.${index}.gender`}
                            component="div"
                            className="invalid-feedback d-block"
                          />
                        </div>

                        {/* Civil ID */}
                        <div className="col-md-4 col-sm-12">
                          <label className="form-label">
                            {t("travel.page3.formLabels.civilResidentId")}
                            <span className="text-danger">
                              {t("travel.page3.formLabels.required")}
                            </span>
                          </label>
                          <Field
                            name={`mem_details.${index}.civil_resident_id`}
                            className={`form-control element-style ${
                              personErrors.civil_resident_id &&
                              personTouched.civil_resident_id
                                ? "is-invalid"
                                : ""
                            }`}
                          />
                          <ErrorMessage
                            name={`mem_details.${index}.civil_resident_id`}
                            component="div"
                            className="invalid-feedback d-block"
                          />
                        </div>

                        {/* Passport Number */}
                        <div className="col-md-4 col-sm-12">
                          <label className="form-label">
                            {t("travel.page3.formLabels.passportNumber")}
                            <span className="text-danger">
                              {t("travel.page3.formLabels.required")}
                            </span>
                          </label>
                          <Field
                            name={`mem_details.${index}.passport_number`}
                            className={`form-control element-style ${
                              personErrors.passport_number &&
                              personTouched.passport_number
                                ? "is-invalid"
                                : ""
                            }`}
                          />
                          <ErrorMessage
                            name={`mem_details.${index}.passport_number`}
                            component="div"
                            className="invalid-feedback d-block"
                          />
                        </div>

                        {/* First person only fields */}
                        {index === 0 && (
                          <>
                            {/* Nationality */}
                            <div className="col-md-4 col-sm-12">
                              <label className="form-label">
                                {t("travel.page3.formLabels.nationality")}
                                <span className="text-danger">
                                  {t("travel.page3.formLabels.required")}
                                </span>
                              </label>
                              <Field
                                as="select"
                                name={`mem_details.${index}.nationality`}
                                className={`form-control element-style ${
                                  personErrors.nationality &&
                                  personTouched.nationality
                                    ? "is-invalid"
                                    : ""
                                }`}
                              >
                                <option value="">{t("travel.page3.formLabels.selectNationality")}</option>
                                {takafulNationalityCompanyMasterData?.map(
                                  (nationality, i) => (
                                    <option key={i} value={nationality.name}>
                                      {nationality.name}
                                    </option>
                                  )
                                )}
                              </Field>
                              <ErrorMessage
                                name={`mem_details.${index}.nationality`}
                                component="div"
                                className="invalid-feedback d-block"
                              />
                            </div>

                            {/* Governate */}
                            <div className="col-md-4 col-sm-12">
                              <label className="form-label">
                                {t("travel.page3.formLabels.governate")}
                                <span className="text-danger">
                                  {t("travel.page3.formLabels.required")}
                                </span>
                              </label>
                              <Field
                                as="select"
                                name={`mem_details.${index}.governate`}
                                className={`form-control element-style ${
                                  personErrors.governate &&
                                  personTouched.governate
                                    ? "is-invalid"
                                    : ""
                                }`}
                                onChange={(e) => {
                                  const selectedGovernate = e.target.value;
                                  setFieldValue(
                                    `mem_details.${index}.governate`,
                                    selectedGovernate
                                  );
                                  setFieldValue(
                                    `mem_details.${index}.wilayat`,
                                    ""
                                  );
                                  dispatch(
                                    getTakafulMasterData(
                                      `wilayat?governorate=${selectedGovernate}`
                                    )
                                  );
                                }}
                              >
                                <option value="">{t("travel.page3.formLabels.selectGovernate")}</option>
                                {takafulGovernateCompanyMasterData?.map(
                                  (governate, i) => (
                                    <option key={i} value={governate.governate}>
                                      {governate.governate}
                                    </option>
                                  )
                                )}
                              </Field>
                              <ErrorMessage
                                name={`mem_details.${index}.governate`}
                                component="div"
                                className="invalid-feedback d-block"
                              />
                            </div>

                            {/* Wilayat */}
                            <div className="col-md-4 col-sm-12">
                              <label className="form-label">
                                {t("travel.page3.formLabels.wilayat")}
                                <span className="text-danger">
                                  {t("travel.page3.formLabels.required")}
                                </span>
                              </label>
                              <Field
                                as="select"
                                name={`mem_details.${index}.wilayat`}
                                className={`form-control element-style ${
                                  personErrors.wilayat && personTouched.wilayat
                                    ? "is-invalid"
                                    : ""
                                }`}
                              >
                                <option value="">{t("travel.page3.formLabels.selectWilayat")}</option>
                                {takafulWilayatCompanyMasterData?.map(
                                  (wilayat, i) => (
                                    <option key={i} value={wilayat.wilayat}>
                                      {wilayat.wilayat}
                                    </option>
                                  )
                                )}
                              </Field>
                              <ErrorMessage
                                name={`mem_details.${index}.wilayat`}
                                component="div"
                                className="invalid-feedback d-block"
                              />
                            </div>

                            {/* Email */}
                            <div className="col-md-4 col-sm-12">
                              <label className="form-label">
                                {t("travel.page3.formLabels.email")}
                                <span className="text-danger">
                                  {t("travel.page3.formLabels.required")}
                                </span>
                              </label>
                              <Field
                                type="email"
                                name={`mem_details.${index}.email`}
                                className={`form-control element-style ${
                                  personErrors.email && personTouched.email
                                    ? "is-invalid"
                                    : ""
                                }`}
                              />
                              <ErrorMessage
                                name={`mem_details.${index}.email`}
                                component="div"
                                className="invalid-feedback d-block"
                              />
                            </div>

                            {/* Mobile No */}
                            <div className="col-md-4 col-sm-12">
                              <label className="form-label">
                                {t("travel.page3.formLabels.mobileNo")}
                                <span className="text-danger">
                                  {t("travel.page3.formLabels.required")}
                                </span>
                              </label>
                              <Field
                                name={`mem_details.${index}.mobile_no`}
                                className={`form-control element-style ${
                                  personErrors.mobile_no &&
                                  personTouched.mobile_no
                                    ? "is-invalid"
                                    : ""
                                }`}
                              />
                              <ErrorMessage
                                name={`mem_details.${index}.mobile_no`}
                                component="div"
                                className="invalid-feedback d-block"
                              />
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })
              }
            </FieldArray>

            {values.mem_details.length > 0 && (
              <div className="text-end">
                <Button type="submit" className="h-btn">
                  {t("travel.page3.buttons.submit")}
                </Button>
              </div>
            )}
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default TakafulPersonDetailsFormFormik;